// Mirror of api/routes/method/period-resolver.js. Keep in sync byte-for-byte
// when either side changes. All dates resolve in Asia/Kuala_Lumpur.

export type AgentAccountPeriod = 'current_week' | 'last_week' | 'current_month' | 'last_month';

export interface ResolvedPeriod {
  label: string;
  from_date: string;   // YYYY-MM-DD
  to_date: string;     // YYYY-MM-DD, inclusive
}

const TZ = 'Asia/Kuala_Lumpur';

function toMYTParts(d: Date): { year: number; month: number; day: number; isoDow: number } {
  const fmt = new Intl.DateTimeFormat('en-CA', {
    timeZone: TZ, year: 'numeric', month: '2-digit', day: '2-digit', weekday: 'short',
  });
  const parts: Record<string, string> = {};
  for (const p of fmt.formatToParts(d)) parts[p.type] = p.value;
  const weekdayOrder: Record<string, number> = { Mon: 1, Tue: 2, Wed: 3, Thu: 4, Fri: 5, Sat: 6, Sun: 7 };
  return {
    year: Number(parts['year']),
    month: Number(parts['month']),
    day: Number(parts['day']),
    isoDow: weekdayOrder[parts['weekday']],
  };
}

function ymd(year: number, month: number, day: number): string {
  return `${year}-${String(month).padStart(2, '0')}-${String(day).padStart(2, '0')}`;
}

function addDaysYmd(isoDate: string, delta: number): string {
  const d = new Date(isoDate + 'T12:00:00Z');
  d.setUTCDate(d.getUTCDate() + delta);
  return d.toISOString().slice(0, 10);
}

function lastDayOfMonth(year: number, month: number): number {
  return new Date(Date.UTC(year, month, 0)).getUTCDate();
}

export function resolvePeriod(setting: AgentAccountPeriod | string | null | undefined, now: Date = new Date()): ResolvedPeriod {
  const { year, month, day, isoDow } = toMYTParts(now);
  const today = ymd(year, month, day);

  switch (setting) {
    case 'current_week': {
      const from = addDaysYmd(today, -(isoDow - 1));
      return { label: 'Current Week', from_date: from, to_date: today };
    }
    case 'last_week': {
      const thisMonday = addDaysYmd(today, -(isoDow - 1));
      const lastMonday = addDaysYmd(thisMonday, -7);
      const lastSunday = addDaysYmd(thisMonday, -1);
      return { label: 'Last Week', from_date: lastMonday, to_date: lastSunday };
    }
    case 'last_month': {
      const prevMonth = month === 1 ? 12 : month - 1;
      const prevYear = month === 1 ? year - 1 : year;
      return {
        label: 'Last Month',
        from_date: ymd(prevYear, prevMonth, 1),
        to_date: ymd(prevYear, prevMonth, lastDayOfMonth(prevYear, prevMonth)),
      };
    }
    case 'current_month':
    default:
      return { label: 'Current Month', from_date: ymd(year, month, 1), to_date: today };
  }
}

export type Granularity = 'weekly' | 'monthly';

export interface PeriodOption {
  key: string;            // 'current_week' | 'last_week' | 'weeks_ago_2'..'weeks_ago_12' | 'current_month' | 'last_month' | 'months_ago_2'..'months_ago_12'
  label: string;
  from_date: string;
  to_date: string;
}

export function granularityOf(setting: string | null | undefined): Granularity {
  switch (setting) {
    case 'current_week':
    case 'last_week':
      return 'weekly';
    case 'current_month':
    case 'last_month':
    default:
      return 'monthly';
  }
}

function startOfWeek(isoDate: string, isoDow: number): string {
  return addDaysYmd(isoDate, -(isoDow - 1));
}

function weeklyOption(today: string, isoDow: number, offset: number): PeriodOption {
  if (offset === 0) {
    return { key: 'current_week', label: 'Current Week', from_date: startOfWeek(today, isoDow), to_date: today };
  }
  const thisMonday = startOfWeek(today, isoDow);
  const from = addDaysYmd(thisMonday, -7 * offset);
  const to = addDaysYmd(from, 6);
  return {
    key: offset === 1 ? 'last_week' : `weeks_ago_${offset}`,
    label: offset === 1 ? 'Last Week' : `${offset} Weeks Ago`,
    from_date: from,
    to_date: to,
  };
}

function monthlyOption(year: number, month: number, today: string, offset: number): PeriodOption {
  if (offset === 0) {
    return { key: 'current_month', label: 'Current Month', from_date: ymd(year, month, 1), to_date: today };
  }
  let y = year;
  let m = month - offset;
  while (m <= 0) { m += 12; y -= 1; }
  return {
    key: offset === 1 ? 'last_month' : `months_ago_${offset}`,
    label: offset === 1 ? 'Last Month' : `${offset} Months Ago`,
    from_date: ymd(y, m, 1),
    to_date: ymd(y, m, lastDayOfMonth(y, m)),
  };
}

export function buildPeriodOptions(granularity: Granularity, count: number, now: Date = new Date()): PeriodOption[] {
  const { year, month, day, isoDow } = toMYTParts(now);
  const today = ymd(year, month, day);
  const opts: PeriodOption[] = [];
  for (let offset = 0; offset <= count; offset++) {
    opts.push(
      granularity === 'weekly'
        ? weeklyOption(today, isoDow, offset)
        : monthlyOption(year, month, today, offset)
    );
  }
  return opts;
}
